/**
 * Progressive Tax Bracket Engine
 * Supports federal income tax, self-employment tax, and state tax calculations.
 * Bracket data based on 2024/2025 IRS published rates.
 */
// ============================================
// 2025 Federal Tax Brackets
// ============================================
const FEDERAL_BRACKETS_2025 = {
    single: [
        { min: 0, max: 11925, rate: 0.10 },
        { min: 11925, max: 48475, rate: 0.12 },
        { min: 48475, max: 103350, rate: 0.22 },
        { min: 103350, max: 197300, rate: 0.24 },
        { min: 197300, max: 250525, rate: 0.32 },
        { min: 250525, max: 626350, rate: 0.35 },
        { min: 626350, max: Infinity, rate: 0.37 },
    ],
    married_filing_jointly: [
        { min: 0, max: 23850, rate: 0.10 },
        { min: 23850, max: 96950, rate: 0.12 },
        { min: 96950, max: 206700, rate: 0.22 },
        { min: 206700, max: 394600, rate: 0.24 },
        { min: 394600, max: 501050, rate: 0.32 },
        { min: 501050, max: 751600, rate: 0.35 },
        { min: 751600, max: Infinity, rate: 0.37 },
    ],
    married_filing_separately: [
        { min: 0, max: 11925, rate: 0.10 },
        { min: 11925, max: 48475, rate: 0.12 },
        { min: 48475, max: 103350, rate: 0.22 },
        { min: 103350, max: 197300, rate: 0.24 },
        { min: 197300, max: 250525, rate: 0.32 },
        { min: 250525, max: 375800, rate: 0.35 },
        { min: 375800, max: Infinity, rate: 0.37 },
    ],
    head_of_household: [
        { min: 0, max: 17000, rate: 0.10 },
        { min: 17000, max: 64850, rate: 0.12 },
        { min: 64850, max: 103350, rate: 0.22 },
        { min: 103350, max: 197300, rate: 0.24 },
        { min: 197300, max: 250500, rate: 0.32 },
        { min: 250500, max: 626350, rate: 0.35 },
        { min: 626350, max: Infinity, rate: 0.37 },
    ],
};
// ============================================
// 2025 Standard Deductions
// ============================================
const STANDARD_DEDUCTIONS_2025 = {
    single: 15000,
    married_filing_jointly: 30000,
    married_filing_separately: 15000,
    head_of_household: 22500,
};
// ============================================
// Self-Employment Tax Constants (2025)
// ============================================
const SE_TAXABLE_FACTOR = 0.9235; // 92.35% of net earnings subject to SE tax
const SE_DEDUCTION_FACTOR = 0.5; // 50% of SE tax is deductible from income
// Social Security wage base (2025)
const SS_WAGE_BASE_2025 = 176100;
const SS_RATE = 0.124;
const MEDICARE_RATE = 0.029;
const ADDITIONAL_MEDICARE_RATE = 0.009; // above $200K single / $250K MFJ
const STATE_TAX_CONFIGS = {
    // No income tax states
    AK: { brackets: [], flatRate: 0 },
    FL: { brackets: [], flatRate: 0 },
    NV: { brackets: [], flatRate: 0 },
    NH: { brackets: [], flatRate: 0 }, // Interest/dividends only
    SD: { brackets: [], flatRate: 0 },
    TN: { brackets: [], flatRate: 0 },
    TX: { brackets: [], flatRate: 0 },
    WA: { brackets: [], flatRate: 0 },
    WY: { brackets: [], flatRate: 0 },
    // Flat rate states
    AZ: { brackets: [], flatRate: 0.025 },
    CO: { brackets: [], flatRate: 0.044 },
    GA: { brackets: [], flatRate: 0.0549 },
    ID: { brackets: [], flatRate: 0.058 },
    IL: { brackets: [], flatRate: 0.0495 },
    IN: { brackets: [], flatRate: 0.0305 },
    IA: { brackets: [], flatRate: 0.038 },
    KY: { brackets: [], flatRate: 0.04 },
    MA: { brackets: [], flatRate: 0.05 },
    MI: { brackets: [], flatRate: 0.0425 },
    MS: { brackets: [], flatRate: 0.047 },
    NC: { brackets: [], flatRate: 0.045 },
    PA: { brackets: [], flatRate: 0.0307 },
    UT: { brackets: [], flatRate: 0.0465 },
    // Progressive bracket states (simplified 2025 estimates)
    CA: {
        brackets: [
            { min: 0, max: 10412, rate: 0.01 },
            { min: 10412, max: 24684, rate: 0.02 },
            { min: 24684, max: 38959, rate: 0.04 },
            { min: 38959, max: 54081, rate: 0.06 },
            { min: 54081, max: 68350, rate: 0.08 },
            { min: 68350, max: 349137, rate: 0.093 },
            { min: 349137, max: 418961, rate: 0.103 },
            { min: 418961, max: 698271, rate: 0.113 },
            { min: 698271, max: 1000000, rate: 0.123 },
            { min: 1000000, max: Infinity, rate: 0.133 },
        ],
    },
    NY: {
        brackets: [
            { min: 0, max: 8500, rate: 0.04 },
            { min: 8500, max: 11700, rate: 0.045 },
            { min: 11700, max: 13900, rate: 0.0525 },
            { min: 13900, max: 80650, rate: 0.0585 },
            { min: 80650, max: 215400, rate: 0.0625 },
            { min: 215400, max: 1077550, rate: 0.0685 },
            { min: 1077550, max: 5000000, rate: 0.0965 },
            { min: 5000000, max: 25000000, rate: 0.103 },
            { min: 25000000, max: Infinity, rate: 0.109 },
        ],
    },
    NJ: {
        brackets: [
            { min: 0, max: 20000, rate: 0.014 },
            { min: 20000, max: 35000, rate: 0.0175 },
            { min: 35000, max: 40000, rate: 0.035 },
            { min: 40000, max: 75000, rate: 0.05525 },
            { min: 75000, max: 500000, rate: 0.0637 },
            { min: 500000, max: 1000000, rate: 0.0897 },
            { min: 1000000, max: Infinity, rate: 0.1075 },
        ],
    },
    OH: {
        brackets: [
            { min: 0, max: 26050, rate: 0 },
            { min: 26050, max: 100000, rate: 0.0275 },
            { min: 100000, max: Infinity, rate: 0.035 },
        ],
    },
    VA: {
        brackets: [
            { min: 0, max: 3000, rate: 0.02 },
            { min: 3000, max: 5000, rate: 0.03 },
            { min: 5000, max: 17000, rate: 0.05 },
            { min: 17000, max: Infinity, rate: 0.0575 },
        ],
    },
    MN: {
        brackets: [
            { min: 0, max: 31690, rate: 0.0535 },
            { min: 31690, max: 104090, rate: 0.068 },
            { min: 104090, max: 183340, rate: 0.0785 },
            { min: 183340, max: Infinity, rate: 0.0985 },
        ],
    },
    OR: {
        brackets: [
            { min: 0, max: 4300, rate: 0.0475 },
            { min: 4300, max: 10750, rate: 0.0675 },
            { min: 10750, max: 125000, rate: 0.0875 },
            { min: 125000, max: Infinity, rate: 0.099 },
        ],
    },
    WI: {
        brackets: [
            { min: 0, max: 14320, rate: 0.035 },
            { min: 14320, max: 28640, rate: 0.044 },
            { min: 28640, max: 315310, rate: 0.053 },
            { min: 315310, max: Infinity, rate: 0.0765 },
        ],
    },
    MO: {
        brackets: [
            { min: 0, max: 1207, rate: 0.02 },
            { min: 1207, max: 2414, rate: 0.025 },
            { min: 2414, max: 3621, rate: 0.03 },
            { min: 3621, max: 4828, rate: 0.035 },
            { min: 4828, max: 6035, rate: 0.04 },
            { min: 6035, max: 7242, rate: 0.045 },
            { min: 7242, max: 8449, rate: 0.05 },
            { min: 8449, max: Infinity, rate: 0.048 },
        ],
    },
};
// US state abbreviations for validation
export const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];
// ============================================
// C-Corporation Tax (Form 1120) — flat 21%
// ============================================
const C_CORP_FEDERAL_RATE = 0.21;
// ============================================
// Entity Type Tax Behavior
// ============================================
/**
 * Returns how a given entity type is taxed.
 * - 'pass_through': Income flows to the owner's personal 1040 (Schedule C / K-1).
 *   Uses individual brackets, filing status matters, SE tax may apply.
 * - 'corporate': Entity files its own return (Form 1120).
 *   Flat 21% federal rate, no filing status, no SE tax.
 */
export function getEntityTaxBehavior(entityType) {
    switch (entityType) {
        case 'sole_proprietorship':
            return { taxMethod: 'pass_through', form: '1040 Schedule C', hasSelfEmploymentTax: true, description: 'Income reported on your personal return (Schedule C). Subject to SE tax.' };
        case 'single_member_llc':
            return { taxMethod: 'pass_through', form: '1040 Schedule C', hasSelfEmploymentTax: true, description: 'Disregarded entity — same as sole proprietorship for tax purposes.' };
        case 'partnership':
            return { taxMethod: 'pass_through', form: '1065 / K-1', hasSelfEmploymentTax: true, description: 'Partnership files 1065. Your K-1 share flows to your 1040. Subject to SE tax.' };
        case 's_corporation':
            return { taxMethod: 'pass_through', form: '1120-S / K-1', hasSelfEmploymentTax: false, description: 'S-Corp files 1120-S. Distributions pass through via K-1. No SE tax (pay yourself reasonable salary instead).' };
        case 'c_corporation':
            return { taxMethod: 'corporate', form: '1120', hasSelfEmploymentTax: false, description: 'Corporation files Form 1120 at flat 21% federal rate. Filing status does not apply.' };
        default:
            return { taxMethod: 'pass_through', form: '1040', hasSelfEmploymentTax: true, description: '' };
    }
}
// ============================================
// Core Calculation Functions
// ============================================
/**
 * Calculate tax through progressive brackets
 */
export function calculateProgressiveTax(taxableIncome, brackets) {
    if (taxableIncome <= 0 || brackets.length === 0) {
        return { total: 0, effectiveRate: 0, marginalRate: 0, breakdown: [] };
    }
    let totalTax = 0;
    let marginalRate = 0;
    const breakdown = [];
    for (const bracket of brackets) {
        if (taxableIncome <= bracket.min)
            break;
        const taxableInThisBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
        const taxForThisBracket = taxableInThisBracket * bracket.rate;
        totalTax += taxForThisBracket;
        marginalRate = bracket.rate;
        breakdown.push({
            bracket: bracket.max === Infinity
                ? `Over ${formatBracketAmount(bracket.min)}`
                : `${formatBracketAmount(bracket.min)} – ${formatBracketAmount(bracket.max)}`,
            rate: bracket.rate,
            taxableInThisBracket: round2(taxableInThisBracket),
            taxForThisBracket: round2(taxForThisBracket),
        });
    }
    return {
        total: round2(totalTax),
        effectiveRate: taxableIncome > 0 ? round4(totalTax / taxableIncome) : 0,
        marginalRate,
        breakdown,
    };
}
/**
 * Calculate self-employment tax with Social Security wage base cap
 */
export function calculateSelfEmploymentTax(netProfit, seRateOverride) {
    if (netProfit <= 0) {
        return { total: 0, taxableBase: 0, effectiveRate: 0 };
    }
    const taxableBase = round2(netProfit * SE_TAXABLE_FACTOR);
    if (seRateOverride !== undefined) {
        const total = round2(taxableBase * (seRateOverride / 100));
        return { total, taxableBase, effectiveRate: netProfit > 0 ? round4(total / netProfit) : 0 };
    }
    // Social Security portion (capped at wage base)
    const ssTaxable = Math.min(taxableBase, SS_WAGE_BASE_2025);
    const ssTax = ssTaxable * SS_RATE;
    // Medicare portion (no cap) + additional Medicare above threshold
    const medicareTax = taxableBase * MEDICARE_RATE;
    const additionalMedicare = Math.max(0, taxableBase - 200000) * ADDITIONAL_MEDICARE_RATE;
    const total = round2(ssTax + medicareTax + additionalMedicare);
    return {
        total,
        taxableBase,
        effectiveRate: netProfit > 0 ? round4(total / netProfit) : 0,
    };
}
/**
 * Full tax calculation — the main entry point
 */
export function calculateTaxProjection(input) {
    const { grossRevenue, totalDeductions, filingStatus, entityType, state, federalRateOverride, stateRateOverride, selfEmploymentRate, applyStandardDeduction = false, } = input;
    // Determine entity tax behavior
    const behavior = getEntityTaxBehavior(entityType);
    const isCorporate = behavior.taxMethod === 'corporate';
    // 1. Calculate net profit (business income after deductions)
    const netProfit = Math.max(0, grossRevenue - totalDeductions);
    // 2. Self-employment tax (only for pass-through entities that are subject to SE)
    const seTax = behavior.hasSelfEmploymentTax
        ? calculateSelfEmploymentTax(netProfit, selfEmploymentRate)
        : { total: 0, taxableBase: 0, effectiveRate: 0 };
    // 3. Calculate AGI adjustments
    // Deduct 50% of SE tax from AGI (IRS allows this above-the-line deduction)
    const seDeduction = behavior.hasSelfEmploymentTax ? round2(seTax.total * SE_DEDUCTION_FACTOR) : 0;
    let taxableIncome = netProfit - seDeduction;
    // 4. Apply standard deduction if requested (only for pass-through / personal returns)
    if (applyStandardDeduction && !isCorporate) {
        const stdDeduction = input.standardDeduction ?? STANDARD_DEDUCTIONS_2025[filingStatus];
        taxableIncome = Math.max(0, taxableIncome - stdDeduction);
    }
    taxableIncome = Math.max(0, taxableIncome);
    // 5. Federal tax
    let federalResult;
    if (federalRateOverride != null && federalRateOverride > 0) {
        // Use flat override from biz_tax_years
        const flatTax = round2(taxableIncome * (federalRateOverride / 100));
        federalResult = {
            total: flatTax,
            effectiveRate: taxableIncome > 0 ? round4(flatTax / taxableIncome) : 0,
            marginalRate: federalRateOverride / 100,
            breakdown: [{
                    bracket: `Flat rate override`,
                    rate: federalRateOverride / 100,
                    taxableInThisBracket: taxableIncome,
                    taxForThisBracket: flatTax,
                }],
        };
    }
    else if (isCorporate) {
        // C-Corporation: flat 21% federal rate (Form 1120)
        const corpTax = round2(taxableIncome * C_CORP_FEDERAL_RATE);
        federalResult = {
            total: corpTax,
            effectiveRate: taxableIncome > 0 ? round4(corpTax / taxableIncome) : 0,
            marginalRate: C_CORP_FEDERAL_RATE,
            breakdown: [{
                    bracket: 'C-Corp flat rate (Form 1120)',
                    rate: C_CORP_FEDERAL_RATE,
                    taxableInThisBracket: taxableIncome,
                    taxForThisBracket: corpTax,
                }],
        };
    }
    else {
        // Pass-through: use individual brackets based on filing status
        const brackets = FEDERAL_BRACKETS_2025[filingStatus] || FEDERAL_BRACKETS_2025.single;
        federalResult = calculateProgressiveTax(taxableIncome, brackets);
    }
    // 6. State tax
    let stateResult;
    if (stateRateOverride != null && stateRateOverride > 0) {
        const flatTax = round2(taxableIncome * (stateRateOverride / 100));
        stateResult = {
            total: flatTax,
            effectiveRate: taxableIncome > 0 ? round4(flatTax / taxableIncome) : 0,
            marginalRate: stateRateOverride / 100,
            breakdown: [{
                    bracket: `Flat rate`,
                    rate: stateRateOverride / 100,
                    taxableInThisBracket: taxableIncome,
                    taxForThisBracket: flatTax,
                }],
        };
    }
    else if (state && STATE_TAX_CONFIGS[state]) {
        const config = STATE_TAX_CONFIGS[state];
        if (config.flatRate !== undefined) {
            const flatTax = round2(taxableIncome * config.flatRate);
            stateResult = {
                total: flatTax,
                effectiveRate: taxableIncome > 0 ? round4(flatTax / taxableIncome) : 0,
                marginalRate: config.flatRate,
                breakdown: config.flatRate > 0 ? [{
                        bracket: `${state} flat rate`,
                        rate: config.flatRate,
                        taxableInThisBracket: taxableIncome,
                        taxForThisBracket: flatTax,
                    }] : [],
            };
        }
        else {
            stateResult = calculateProgressiveTax(taxableIncome, config.brackets);
        }
    }
    else {
        stateResult = { total: 0, effectiveRate: 0, marginalRate: 0, breakdown: [] };
    }
    // 7. Totals
    const totalTax = round2(federalResult.total + seTax.total + stateResult.total);
    const totalEffectiveRate = grossRevenue > 0 ? round4(totalTax / grossRevenue) : 0;
    const netIncomeAfterTax = round2(netProfit - totalTax);
    return {
        grossRevenue,
        totalDeductions,
        taxableIncome: round2(taxableIncome),
        federalTax: federalResult.total,
        federalEffectiveRate: federalResult.effectiveRate,
        federalMarginalRate: federalResult.marginalRate,
        federalBracketBreakdown: federalResult.breakdown,
        selfEmploymentTax: seTax.total,
        selfEmploymentEffectiveRate: seTax.effectiveRate,
        selfEmploymentTaxableBase: seTax.taxableBase,
        stateTax: stateResult.total,
        stateEffectiveRate: stateResult.effectiveRate,
        stateBracketBreakdown: stateResult.breakdown,
        totalTax,
        totalEffectiveRate,
        netIncomeAfterTax,
        quarterlyEstimate: round2(totalTax / 4),
        monthlySetAside: round2(totalTax / 12),
    };
}
// ============================================
// Helpers
// ============================================
function round2(n) {
    return Math.round(n * 100) / 100;
}
function round4(n) {
    return Math.round(n * 10000) / 10000;
}
function formatBracketAmount(n) {
    return `$${n.toLocaleString('en-US')}`;
}
/**
 * Get the list of supported states with tax info
 */
export function getSupportedStates() {
    return US_STATES.map(code => {
        const config = STATE_TAX_CONFIGS[code];
        if (!config)
            return { code, hasIncomeTax: true, type: 'progressive' }; // Unknown = assume progressive
        if (config.flatRate === 0)
            return { code, hasIncomeTax: false, type: 'none' };
        if (config.flatRate !== undefined)
            return { code, hasIncomeTax: true, type: 'flat' };
        return { code, hasIncomeTax: true, type: 'progressive' };
    });
}
/**
 * Get standard deduction for a filing status
 */
export function getStandardDeduction(filingStatus) {
    return STANDARD_DEDUCTIONS_2025[filingStatus] || STANDARD_DEDUCTIONS_2025.single;
}
/**
 * Get federal brackets for a filing status (for UI display)
 */
export function getFederalBrackets(filingStatus) {
    return FEDERAL_BRACKETS_2025[filingStatus] || FEDERAL_BRACKETS_2025.single;
}
/**
 * Get state brackets for a state (for UI display)
 */
export function getStateBrackets(state) {
    const config = STATE_TAX_CONFIGS[state];
    if (!config)
        return null;
    if (config.brackets.length > 0)
        return config.brackets;
    return null;
}
//# sourceMappingURL=tax-brackets.js.map